
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

// Declare the global driver variable from the CDN script
declare const driver: any;

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private router: Router = inject(Router);

  constructor() {}

  /**
   * Helper to safely locate the driver function from the global scope,
   * handling different UMD/IIFE structures.
   */
  private getDriverFn(): any {
    if (typeof driver !== 'undefined') {
      // In some builds, it might be nested under driver.js.driver
      if (driver && driver.js && typeof driver.js.driver === 'function') {
        return driver.js.driver;
      }
      // In others, 'driver' itself is the function
      if (typeof driver === 'function') {
        return driver;
      }
    }
    return null;
  }

  public startTour() {
    const driverFn = this.getDriverFn();

    if (!driverFn) {
        console.warn('Driver.js not loaded. Tour cannot start.');
        return;
    }

    const currentUrl = this.router.url;
    let steps: any[] = [];

    // Determine steps based on current route
    if (currentUrl.includes('/admin/dashboard')) {
        steps = this.getDashboardSteps();
    } else if (currentUrl.includes('/admin/pharmacy')) {
        steps = this.getPharmacySteps();
    } else if (currentUrl.includes('/admin/tele-medicine')) {
        steps = this.getTelemedSteps();
    } else if (currentUrl.includes('/admin/customers')) {
        steps = this.getCustomerSteps();
    } else {
        steps = this.getGenericSteps();
    }

    // Initialize Driver.js with the specific steps for this page
    const driverObj = driverFn({
        showProgress: true,
        animate: true,
        allowClose: true,
        nextBtnText: 'ถัดไป (Next)',
        prevBtnText: 'ย้อนกลับ (Prev)',
        doneBtnText: 'เสร็จสิ้น (Done)',
        popoverClass: 'driverjs-theme', // Custom class defined in index.html
        steps: steps // Pass steps here for v1.x compatibility
    });

    driverObj.drive();
  }

  private getDashboardSteps() {
    return [
      { 
        popover: { 
          title: 'ยินดีต้อนรับสู่ CHUAMKAN (Welcome)', 
          description: 'ระบบปฏิบัติการทางการแพทย์สำหรับคลินิกกัญชาที่ครบวงจรที่สุด นี่คือหน้าภาพรวม (Dashboard) ของคุณ' 
        } 
      },
      { 
        element: '#kpi-section', 
        popover: { 
          title: 'สรุปผลประกอบการ (KPIs)', 
          description: 'ดูยอดขาย, จำนวนผู้ป่วยใหม่, และสถานะใบสั่งยาได้ทันทีที่นี่' 
        } 
      },
      { 
        element: '#plan-usage-card', 
        popover: { 
          title: 'สถานะแพ็กเกจ (Plan Usage)', 
          description: 'ตรวจสอบจำนวนเคสที่ใช้ไปและวันตัดรอบบิล เพื่อวางแผนการใช้งานได้อย่างเหมาะสม' 
        } 
      },
      { 
        element: '#quick-actions-card', 
        popover: { 
          title: 'เมนูลัด (Quick Actions)', 
          description: 'เข้าถึงฟังก์ชันที่ใช้บ่อย เช่น ลงทะเบียนผู้ป่วย หรือเข้าห้องตรวจแพทย์ ได้ในคลิกเดียว' 
        } 
      }
    ];
  }

  private getPharmacySteps() {
    return [
      { 
        popover: { 
          title: 'ห้องจ่ายยา (Pharmacy Station)', 
          description: 'จัดการการจ่ายยาตามใบสั่งแพทย์ และบันทึกข้อมูลเพื่อส่งรายงาน ภท.28' 
        } 
      },
      { 
        element: '#pharmacy-queue-list', 
        popover: { 
          title: 'คิวรอรับยา (Queue)', 
          description: 'รายชื่อผู้ป่วยที่ผ่านการตรวจและได้รับอนุมัติใบสั่งยาแล้วจะปรากฏที่นี่' 
        } 
      },
      { 
        element: '#prescription-detail-panel', 
        popover: { 
          title: 'รายละเอียดใบสั่งยา', 
          description: 'ตรวจสอบรายการยา, จำนวน, และวิธีใช้ตามที่แพทย์สั่ง' 
        } 
      },
      { 
        element: '#dispense-action-btn', 
        popover: { 
          title: 'บันทึกการจ่าย (Dispense)', 
          description: 'กดปุ่มนี้เมื่อจัดยาเสร็จสิ้น เพื่อตัดสต็อกและบันทึกลงรายงาน Compliance โดยอัตโนมัติ' 
        } 
      }
    ];
  }

  private getTelemedSteps() {
    return [
      { 
        popover: { 
          title: 'ห้องตรวจแพทย์ (Doctor Station)', 
          description: 'ศูนย์กลางสำหรับแพทย์ในการตรวจรักษาทางไกลและอนุมัติใบสั่งยา' 
        } 
      },
      { 
        element: '#patient-queue-list', 
        popover: { 
          title: 'คิวรอตรวจ (Patient Queue)', 
          description: 'ผู้ป่วยที่ลงทะเบียนผ่าน Kiosk จะมารอคิวที่นี่ พร้อมอาการเบื้องต้น' 
        } 
      },
      { 
        element: '#video-call-btn', 
        popover: { 
          title: 'เริ่มวิดีโอคอล (Start Call)', 
          description: 'กดเพื่อเริ่มสนทนากับผู้ป่วย ระบบจะส่งสัญญาณไปที่ Kiosk ของผู้ป่วยทันที' 
        } 
      },
      { 
        element: '#review-btn', 
        popover: { 
          title: 'ตรวจและสั่งยา (Review)', 
          description: 'เปิดดูประวัติผู้ป่วย, วินิจฉัยโรค, และออกใบรับรองแพทย์ดิจิทัล (Digital Certificate)' 
        } 
      }
    ];
  }

  private getCustomerSteps() {
      return [
          {
              popover: {
                  title: 'ทะเบียนผู้ป่วย (Patient Database)',
                  description: 'ค้นหาประวัติ, ดูข้อมูลการแพ้ยา, และตรวจสอบโควต้าคงเหลือของผู้ป่วย'
              }
          },
          {
              element: '#add-patient-btn',
              popover: {
                  title: 'ลงทะเบียนใหม่',
                  description: 'เพิ่มผู้ป่วยรายใหม่เข้าสู่ระบบด้วยตนเอง (กรณีไม่ได้ใช้ Kiosk)'
              }
          },
          {
              element: '#search-box',
              popover: {
                  title: 'ค้นหาด่วน',
                  description: 'ค้นหาด้วยชื่อ, เบอร์โทร, หรือเลขบัตรประชาชน'
              }
          }
      ];
  }

  private getGenericSteps() {
      return [
          {
              popover: {
                  title: 'แนะนำการใช้งาน',
                  description: 'ระบบนี้ถูกออกแบบมาให้ใช้งานง่าย คุณสามารถเลือกเมนูทางด้านซ้ายเพื่อเข้าถึงฟังก์ชันต่างๆ'
              }
          },
          {
              element: 'app-sidebar',
              popover: {
                  title: 'เมนูหลัก (Main Menu)',
                  description: 'เข้าถึง Dashboard, ห้องตรวจ, ห้องยา, และรายงานต่างๆ ได้จากที่นี่'
              }
          }
      ];
  }
}
